/**
 * Professional Audio System Types
 * Fr00t Sacred Audio Architecture
 * 
 * This file defines all types for the professional audio generation system,
 * including sacred frequencies, scales, chords, and audio parameters.
 */

// ============================================================================
// CORE AUDIO TYPES
// ============================================================================

/**
 * Audio waveform types supported by the Web Audio API
 */
export type WaveformType = 'sine' | 'square' | 'sawtooth' | 'triangle';

/**
 * Audio state for tracking playback and initialization
 */
export interface AudioState {
  /** Whether the audio context is initialized */
  isInitialized: boolean;
  /** Whether audio is currently playing */
  isPlaying: boolean;
  /** Current frequency being played (Hz) */
  currentFrequency: number;
  /** Volume level in decibels (-40 to 0) */
  volume: number;
}

/**
 * ADSR Envelope parameters for professional sound shaping
 * Attack-Decay-Sustain-Release controls the amplitude over time
 */
export interface ADSREnvelope {
  /** Attack time: Time to reach peak amplitude (seconds) */
  attack: number;
  /** Decay time: Time to reach sustain level (seconds) */
  decay: number;
  /** Sustain level: Amplitude during hold (0-1) */
  sustain: number;
  /** Release time: Time to fade to silence (seconds) */
  release: number;
}

/**
 * Audio effect parameters for sound enhancement
 */
export interface AudioEffects {
  /** Reverb amount (0-1) */
  reverb?: number;
  /** Delay amount (0-1) */
  delay?: number;
  /** Chorus amount (0-1) */
  chorus?: number;
  /** Low-pass filter frequency (Hz) */
  lowPassFilter?: number;
  /** High-pass filter frequency (Hz) */
  highPassFilter?: number;
}

// ============================================================================
// SACRED FREQUENCY DEFINITIONS
// ============================================================================

/**
 * Sacred frequency mapping for Fr00t numerology
 * F=6 (432Hz), R=9 (528Hz), 0=0 (396Hz), T=2 (639Hz)
 */
export interface Fr00tFrequencies {
  F: number; // 432 Hz - Harmony and balance
  R: number; // 528 Hz - C5
  0: number; // 396 Hz - G4
  T: number; // 639 Hz - Connection and relationships
}

/**
 * Chakra frequency mapping (7 energy centers)
 */
export interface ChakraFrequencies {
  root: number;      // 396 Hz - Muladhara
  sacral: number;    // 417 Hz - Svadhisthana
  solar: number;     // 528 Hz - Manipura
  heart: number;     // 639 Hz - Anahata
  throat: number;    // 741 Hz - Vishuddha
  third_eye: number; // 852 Hz - Ajna
  crown: number;     // 963 Hz - Sahasrara
}

/**
 * Solfeggio frequency mapping (ancient healing frequencies)
 */
export interface SolfeggioFrequencies {
  ut: number;  // 396 Hz - Liberating guilt and fear
  re: number;  // 417 Hz - Undoing situations and facilitating change
  mi: number;  // 528 Hz - Mi (historical Solfeggio note)
  fa: number;  // 639 Hz - Connecting/relationships
  sol: number; // 741 Hz - Sol (historical Solfeggio note)
  la: number;  // 852 Hz - Returning to spiritual order
}

/**
 * Complete sacred frequency collection
 */
export interface SacredFrequencies {
  fr00t: Fr00tFrequencies;
  chakras: ChakraFrequencies;
  solfeggio: SolfeggioFrequencies;
}

// ============================================================================
// SCALES AND CHORDS
// ============================================================================

/**
 * Scale type identifier
 */
export type ScaleType = 
  | 'solfeggio'
  | 'chakra'
  | 'fr00t'
  | 'healing'
  | 'ascending'
  | 'golden'
  | 'harmonic'
  | 'pentatonic';

/**
 * Chord type identifier
 */
export type ChordType = 
  | 'fr00t'
  | 'harmony'
  | 'healing'
  | 'chakra'
  | 'love'
  | 'void'
  | 'manifest'
  | 'protect'
  | 'power'
  | 'wisdom';

/**
 * Scale definition with frequencies and metadata
 */
export interface ScaleDefinition {
  /** Unique identifier for the scale */
  id: ScaleType;
  /** Display name */
  name: string;
  /** Description of the scale's purpose */
  description: string;
  /** Array of frequencies in Hz */
  frequencies: number[];
  /** Associated color for UI representation */
  color?: string;
  /** Tradition or origin of the scale */
  tradition?: string;
}

/**
 * Chord definition with frequencies and metadata
 */
export interface ChordDefinition {
  /** Unique identifier for the chord */
  id: ChordType;
  /** Display name */
  name: string;
  /** Description of the chord's purpose */
  description: string;
  /** Array of frequencies in Hz to play simultaneously */
  frequencies: number[];
  /** Associated color for UI representation */
  color?: string;
  /** Spiritual or energetic purpose */
  purpose?: string;
}

// ============================================================================
// PLAYBACK PARAMETERS
// ============================================================================

/**
 * Parameters for playing a single tone
 */
export interface ToneParameters {
  /** Frequency in Hz */
  frequency: number;
  /** Duration in seconds */
  duration?: number;
  /** Waveform type */
  waveform?: WaveformType;
  /** Volume (0-1) */
  volume?: number;
  /** ADSR envelope */
  envelope?: ADSREnvelope;
  /** Audio effects */
  effects?: AudioEffects;
}

/**
 * Parameters for playing a chord
 */
export interface ChordParameters {
  /** Chord type or array of frequencies */
  chord: ChordType | number[];
  /** Duration in seconds */
  duration?: number;
  /** Waveform type */
  waveform?: WaveformType;
  /** Volume (0-1) */
  volume?: number;
  /** ADSR envelope */
  envelope?: ADSREnvelope;
  /** Audio effects */
  effects?: AudioEffects;
}

/**
 * Parameters for playing a scale
 */
export interface ScaleParameters {
  /** Scale type or array of frequencies */
  scale: ScaleType | number[];
  /** Duration per note in milliseconds */
  noteDuration?: number;
  /** Waveform type */
  waveform?: WaveformType;
  /** Volume (0-1) */
  volume?: number;
  /** ADSR envelope */
  envelope?: ADSREnvelope;
  /** Audio effects */
  effects?: AudioEffects;
}

// ============================================================================
// SABIAN SYMBOL AUDIO TYPES
// ============================================================================

/**
 * Sabian symbol audio parameters
 * Separate from sacred frequencies per project requirements
 */
export interface SabianSymbolAudio {
  /** Sabian symbol degree (1-360) */
  degree: number;
  /** Zodiac sign (Aries to Pisces) */
  sign: string;
  /** Base frequency calculated from degree */
  baseFrequency: number;
  /** Harmonic frequencies derived from symbol meaning */
  harmonics: number[];
  /** Duration of the symbol sound */
  duration: number;
  /** Associated element (Fire, Earth, Air, Water) */
  element: 'Fire' | 'Earth' | 'Air' | 'Water';
}

/**
 * Parameters for generating Sabian symbol audio
 */
export interface SabianAudioParameters {
  /** Sabian symbol degree (1-360) */
  degree: number;
  /** Duration in seconds */
  duration?: number;
  /** Volume (0-1) */
  volume?: number;
  /** Include harmonic overtones */
  includeHarmonics?: boolean;
}

// ============================================================================
// AUDIO CONTROL INTERFACES
// ============================================================================

/**
 * Main audio control interface for sacred audio system
 */
export interface SacredAudioControls {
  /** Initialize the audio context */
  initializeAudio: () => Promise<void>;
  /** Initialize sacred audio (alias) */
  initializeSacredAudio: () => Promise<void>;
  /** Play a single sacred tone */
  playSacredTone: (frequency: number, duration?: number, waveform?: WaveformType) => void;
  /** Play breath awareness tone - optimized for grounding and centering with frequency balance */
  playBreathAwarenessTone: (duration?: number) => void;
  /** Play golden light tone - optimized for warmth and healing meditation */
  playGoldenLightTone: (duration?: number) => void;
  /** Play a sacred chord */
  playSacredChord: (chord: ChordType | string) => void;
  /** Play a sacred scale */
  playSacredScale: (scale: ScaleType | string) => void;
  /** Stop all audio playback */
  stopAudio: () => void;
  /** Set master volume */
  setVolume: (volume: number) => void;
  /** Generate a sacred frequency from a numerology number */
  generateSacredFrequency: (number: number) => number;
}

/**
 * Sabian symbol audio control interface
 */
export interface SabianAudioControls {
  /** Initialize Sabian audio system */
  initializeSabianAudio: () => Promise<void>;
  /** Play Sabian symbol sound */
  playSabianSymbol: (params: SabianAudioParameters) => void;
  /** Generate Sabian symbol frequencies */
  generateSabianFrequencies: (degree: number) => SabianSymbolAudio;
  /** Stop Sabian audio */
  stopSabianAudio: () => void;
}

// ============================================================================
// COMPLETE HOOK RETURN TYPES
// ============================================================================

/**
 * Return type for useSacredAudio hook
 */
export type UseSacredAudioReturn = AudioState & SacredAudioControls & {
  /** Sacred tone frequencies */
  sacredTones: Fr00tFrequencies;
};

/**
 * Return type for useSabianAudio hook
 */
export type UseSabianAudioReturn = AudioState & SabianAudioControls;

// ============================================================================
// CONSTANTS AND DEFAULTS
// ============================================================================

/**
 * Default ADSR envelope for smooth natural tones
 */
export const DEFAULT_ENVELOPE: ADSREnvelope = {
  attack: 0.05,
  decay: 0.1,
  sustain: 0.7,
  release: 0.3
};

/**
 * Default audio durations
 */
export const DEFAULT_DURATIONS = {
  tone: 2.0,      // seconds
  chord: 3.0,     // seconds
  note: 0.6,      // seconds (for scales)
  sabian: 4.0     // seconds
};

/**
 * Volume range in decibels
 */
export const VOLUME_RANGE = {
  min: -40,
  max: 0,
  default: -20
};

